import { ColDef, IRowNode } from 'ag-grid-community'
import { TFunction } from 'i18next'
import { compact, uniq } from 'lodash/fp'
import { FC } from 'react'
import { Api, Resource as ApiResource } from 'shared/src/api'
import {
  AffiliateDomain,
  AffiliateDomainInput,
  Brand,
  BrandFeature,
  BrandFeatureInput,
  BrandInput,
  CategoryInput,
  CategoryWithUserIds,
  CmsUser,
  Config,
  CreatorFeature,
  CreatorFeatureInput,
  CreatorLaunchImage,
  CreatorLaunchImageInput,
  Edit,
  EditInput,
  Label,
  LabelInput,
  Media,
  MediaInput,
  MediaState,
  ProductFeature,
  ProductFeatureInput,
  ProductInput,
  ProductWithImageAndRelatedProductIds,
  RedemptionRequest,
  RedemptionRequestInput,
  Tag,
  TagInput,
  UserInput,
} from 'shared/src/api/types'

import { priceStringToCents } from '../utils/price'
import {
  Errors,
  Validator,
  optionalUrlValidator,
  presentNumberValidator,
  presentStringValidator,
  presentUrlValidator,
  presentValidator,
} from '../utils/validators'
import {
  AffiliateDomainForm,
  FormValues as AffiliateDomainFormValues,
} from './AffiliateDomainForm'
import { BrandsExtraButtons } from './BrandsExtraButtons'
import { BrandsForm } from './BrandsForm'
import {
  CategoriesForm,
  FormValues as CategoriesFormValues,
} from './CategoriesForm'
import { CreatorFeatureForm } from './CreatorFeatureForm'
import { CreatorLaunchImageForm } from './CreatorLaunchImageForm'
import { EditForm, FormValues as EditFormValues } from './EditForm'
import { LabelForm, FormValues as LabelFormValues } from './LabelForm'
import { MediaExtraButtons } from './MediaExtraButtons'
import { MediaForm, FormValues as MediaFormValues } from './MediaForm'
import { ProductFeatureForm } from './ProductFeatureForm'
import { ProductForm, FormValues as ProductFormValues } from './ProductForm'
import { ProductsExtraButtons } from './ProductsExtraButtons'
import { ProductsExtraHeader } from './ProductsExtraHeader'
import {
  RedemptionRequestForm,
  FormValues as RedemptionRequestFormValues,
} from './RedemptionRequestForm'
import { TagForm, FormValues as TagFormValues } from './TagForm'
import { FormValues as UserFormValues, UsersForm } from './UsersForm'
import { productValidator } from './account/validator'
import { BrandFeatureForm } from './BrandFeatureForm'

export const resourceOrder = [
  'affiliateDomains',
  'brands',
  'categories',
  'edits',
  'users',
  'media',
  'products',
  'tags',
  'creatorLaunchImages',
  'brandFeatures',
  'creatorFeatures',
  'productFeatures',
  'labels',
  'redemptionRequests',
] as const

export type ResourceName = (typeof resourceOrder)[number]

export interface FormProps<TFormValues> {
  id?: number
  values: TFormValues
  onChange: (values: TFormValues) => void
  errors: Errors<TFormValues>
  disabled?: boolean
}

export interface ResourceType<
  TModel extends { id: number; createdAt: Date; updatedAt: Date },
  TModelInput,
  TFormValues
> {
  pluralModelName: string
  singularModelName: string
  title: (model: TModel) => string
  columns: ColDef<TModel>[]
  api: ApiResource<TModel, TModelInput>
  IndexExtraButtons?: FC<{ reload: () => void }>
  IndexExtraHeader?: FC
  Form: FC<FormProps<TFormValues>>
  saveButtonTitle?: (values: TFormValues) => string
  emptyInput: (query?: string) => TFormValues
  inputFromExisting: (model: TModel) => TFormValues
  formValuesToModelInput: (values: TFormValues) => TModelInput
  formValidator: Validator<TFormValues>
  canDelete?: boolean // defaults to true
  // whether the resource should be shown in the main list of
  // resources, allowing creating/editing/deleting by creators
  availableToCreators?: boolean
  // whether the resource should be editable by creators when it is
  // being selected in a dropdown in a form for another resource
  editableByCreators?: boolean
}

const idColumn = <TModel extends { id: number }>(
  t: TFunction
): ColDef<TModel> => ({
  headerName: t('admin.resources.general.columns.id'),
  field: 'id',
  type: ['number'],
})

const createdAtColumn = <TModel extends { createdAt: Date }>(
  t: TFunction,
  sortBy?: 'asc' | 'desc'
): ColDef<TModel> => ({
  headerName: t('admin.resources.general.columns.createdAt'),
  field: 'createdAt',
  type: ['date'],
  width: 150,
  sort: sortBy ?? null,
})

const affiliateDomainsResource = (
  api: Api,
  t: TFunction
): ResourceType<
  AffiliateDomain,
  AffiliateDomainInput,
  AffiliateDomainFormValues
> => ({
  pluralModelName: t('admin.resources.affiliateDomains.namePlural'),
  singularModelName: t('admin.resources.affiliateDomains.nameSingular'),
  title: ({ domain }) => domain,
  columns: [
    {
      headerName: t('admin.resources.affiliateDomains.fields.domain'),
      field: 'domain',
    },
    {
      headerName: t('admin.resources.affiliateDomains.fields.rate'),
      field: 'rate',
      type: ['number'],
    },
    idColumn(t),
  ],
  api: api.affiliateDomains,
  Form: AffiliateDomainForm,
  emptyInput: (query) => ({ domain: query }),
  inputFromExisting: ({ domain, rate }) => ({
    domain,
    rate: rate.toString(),
  }),
  formValuesToModelInput: ({ domain, rate }) => ({
    domain: domain!,
    rate: rate ? parseInt(rate) : 0,
  }),
  formValidator: {
    domain: presentValidator(
      t('admin.resources.affiliateDomains.fields.domain'),
      t
    ),
    rate: presentValidator(
      t('admin.resources.affiliateDomains.fields.rate'),
      t
    ),
  },
})

const brandsResource = (
  api: Api,
  t: TFunction
): ResourceType<Brand, BrandInput, Partial<BrandInput>> => ({
  pluralModelName: t('admin.resources.brands.namePlural'),
  singularModelName: t('admin.resources.brands.nameSingular'),
  title: ({ name }) => name,
  columns: [
    {
      headerName: t('admin.resources.brands.fields.name'),
      field: 'name',
    },
    {
      headerName: t('admin.resources.brands.fields.domain'),
      field: 'domain',
    },
    idColumn(t),
    createdAtColumn(t),
  ],
  api: api.brands,
  Form: BrandsForm,
  emptyInput: (query) => ({ name: query }),
  inputFromExisting: (model) => model,
  formValidator: {
    name: presentStringValidator(t('admin.resources.brands.fields.name'), t),
  },
  formValuesToModelInput: (values) => values as BrandInput,
  canDelete: false,
  IndexExtraButtons: BrandsExtraButtons,
})

const categoriesResource = (
  api: Api,
  t: TFunction
): ResourceType<CategoryWithUserIds, CategoryInput, CategoriesFormValues> => ({
  pluralModelName: t('admin.resources.categories.namePlural'),
  singularModelName: t('admin.resources.categories.nameSingular'),
  title: ({ name }) => name,
  columns: [
    {
      headerName: t('admin.resources.categories.fields.name'),
      field: 'name',
    },
    idColumn(t),
    createdAtColumn(t),
  ],
  api: api.categories,
  Form: CategoriesForm,
  emptyInput: (query) => ({
    name: query,
  }),
  inputFromExisting: (model) => ({
    ...model,
    position: model.position?.toString(),
  }),
  formValidator: {
    name: presentStringValidator(
      t('admin.resources.categories.fields.name'),
      t
    ),
    imageId: presentNumberValidator(
      t('admin.resources.categories.fields.imageId'),
      t
    ),
  },
  formValuesToModelInput: ({ name, imageId, position }) => ({
    name: name!,
    imageId: imageId!,
    position: position ? parseInt(position) : null,
  }),
})

const editsResource = (
  api: Api,
  t: TFunction
): ResourceType<Edit, EditInput, EditFormValues> => ({
  pluralModelName: t('admin.resources.edits.namePlural'),
  singularModelName: t('admin.resources.edits.nameSingular'),
  title: ({ name }) => name,
  columns: [
    {
      headerName: t('admin.resources.edits.fields.name'),
      field: 'name',
    },
    {
      headerName: t('admin.resources.edits.fields.position'),
      field: 'position',
      type: ['number'],
    },
    {
      headerName: t('admin.resources.edits.fields.uses'),
      field: 'uses',
      type: ['number'],
    },
    {
      headerName: t('admin.resources.edits.fields.views'),
      field: 'views',
      type: ['number'],
    },
    {
      headerName: t('admin.resources.edits.fields.isRodeoExclusive'),
      field: 'isRodeoExclusive',
    },
    createdAtColumn(t),
  ],
  api: api.edits,
  Form: EditForm,
  emptyInput: (query) => ({
    name: query,
    isRodeoExclusive: false,
  }),
  inputFromExisting: (model) => ({
    ...model,
    position: model.position?.toString(),
  }),
  formValidator: {
    name: presentStringValidator(t('admin.resources.edits.fields.name'), t),
  },
  formValuesToModelInput: ({
    name,
    position,
    isRodeoExclusive,
    coverImageId,
  }) => ({
    name: name!,
    position: position ? parseInt(position) : null,
    isRodeoExclusive,
    coverImageId: coverImageId ?? null,
  }),
})

const usersResource = (
  api: Api,
  t: TFunction
): ResourceType<CmsUser, UserInput, UserFormValues> => ({
  pluralModelName: t('admin.resources.users.namePlural'),
  singularModelName: t('admin.resources.users.nameSingular'),
  title: ({ username, email }) => `${username ?? 'no username'} (${email})`,
  columns: [
    createdAtColumn(t, 'desc'),
    {
      headerName: t('admin.resources.users.fields.username'),
      field: 'username',
    },
    {
      headerName: t('admin.resources.users.fields.email'),
      field: 'email',
    },
    {
      headerName: t('admin.resources.users.fields.follows'),
      field: 'follows',
      type: ['number'],
    },
    {
      headerName: t('admin.resources.users.fields.media'),
      field: 'media',
      type: ['number'],
    },
    {
      headerName: t('admin.resources.users.fields.lastPublishedMediaDate'),
      field: 'lastPublishedMediaDate',
      type: ['date'],
      width: 130,
    },
    {
      headerName: t('admin.resources.users.fields.mediaViews'),
      field: 'mediaViews',
      type: ['number'],
    },
    {
      headerName: t('admin.resources.users.fields.editViews'),
      field: 'behindTheGateViews',
      type: ['number'],
    },
    {
      headerName: t('admin.resources.users.fields.saves'),
      field: 'saves',
      type: ['number'],
    },
    {
      headerName: t('admin.resources.users.fields.isFeatured'),
      field: 'isFeatured',
    },
    {
      headerName: t('admin.resources.users.fields.creatorFund'),
      field: 'isExcludedFromCreatorFund',
      valueGetter: ({ data }) =>
        data?.isCreator && !data?.isExcludedFromCreatorFund,
    },
    idColumn(t),
  ],
  api: api.users,
  Form: UsersForm,
  emptyInput: (query) => ({
    username: query,
    isCreator: true,
    isExcludedFromCreatorFund: false,
    isFeatured: false,
    isAssisted: false,
    isVerified: false,
    isDefaultFollow: false,
    isAdmin: false,
    isSuperAdmin: false,
    isStealth: false,
    showMerch: false,
    shouldScrapeInstagram: true,
    noSms: false,
    defaultToLinksPage: false,
    defaultToFullScreen: false,
  }),
  inputFromExisting: ({ categoryIds, ...model }) => ({
    ...model,
    categoryId1: categoryIds[0],
    categoryId2: categoryIds[1],
    categoryId3: categoryIds[2],
  }),
  formValidator: {
    email: presentStringValidator(t('admin.resources.users.fields.email'), t),
    homepage: optionalUrlValidator(
      t('admin.resources.users.fields.homepage'),
      t
    ),
    instagramUrl: optionalUrlValidator(
      t('admin.resources.users.fields.instagramUrl'),
      t
    ),
    pinterestUrl: optionalUrlValidator(
      t('admin.resources.users.fields.pinterestUrl'),
      t
    ),
    amazonUrl: optionalUrlValidator(
      t('admin.resources.users.fields.amazonUrl'),
      t
    ),
    tiktokUrl: optionalUrlValidator(
      t('admin.resources.users.fields.tiktokUrl'),
      t
    ),
    spotifyUrl: optionalUrlValidator(
      t('admin.resources.users.fields.spotifyUrl'),
      t
    ),
    goodreadsUrl: optionalUrlValidator(
      t('admin.resources.users.fields.goodreadsUrl'),
      t
    ),
    snapchatUrl: optionalUrlValidator(
      t('admin.resources.users.fields.snapchatUrl'),
      t
    ),
    etsyUrl: optionalUrlValidator(t('admin.resources.users.fields.etsyUrl'), t),
  },
  formValuesToModelInput: ({
    username,
    email,
    phoneNumber,
    isCreator,
    isExcludedFromCreatorFund,
    isFeatured,
    isAssisted,
    isVerified,
    isDefaultFollow,
    isAdmin,
    isSuperAdmin,
    isStealth,
    showMerch,
    shouldScrapeInstagram,
    noSms,
    brandId,
    imageId,
    backgroundImageId,
    bio,
    defaultToLinksPage,
    defaultToFullScreen,
    homepage,
    instagramUrl,
    pinterestUrl,
    amazonUrl,
    tiktokUrl,
    spotifyUrl,
    goodreadsUrl,
    snapchatUrl,
    etsyUrl,
    youtubeUrl,
    twitterUrl,
    publicEmail,
    facebookUrl,
    applePodcastsUrl,
    categoryId1,
    categoryId2,
    categoryId3,
  }) => ({
    username: username ?? null,
    email: email!,
    phoneNumber: phoneNumber ?? null,
    isCreator,
    isExcludedFromCreatorFund,
    isFeatured,
    isAssisted,
    isVerified,
    isDefaultFollow,
    isAdmin,
    isSuperAdmin,
    isStealth,
    showMerch,
    shouldScrapeInstagram,
    noSms,
    brandId: brandId ?? null,
    imageId: imageId ?? null,
    backgroundImageId: backgroundImageId ?? null,
    bio: bio ?? null,
    defaultToLinksPage,
    defaultToFullScreen,
    homepage: homepage!,
    instagramUrl: instagramUrl!,
    pinterestUrl: pinterestUrl!,
    amazonUrl: amazonUrl!,
    tiktokUrl: tiktokUrl!,
    spotifyUrl: spotifyUrl!,
    goodreadsUrl: goodreadsUrl!,
    snapchatUrl: snapchatUrl!,
    etsyUrl: etsyUrl!,
    youtubeUrl: youtubeUrl!,
    twitterUrl: twitterUrl!,
    publicEmail: publicEmail!,
    facebookUrl: facebookUrl!,
    applePodcastsUrl: applePodcastsUrl!,
    mediaIds: [],
    categoryIds: uniq(compact([categoryId1, categoryId2, categoryId3])),
  }),
})

const saveButtonTranslationStrings: Record<MediaState, string> = {
  adminDraft: 'admin.resources.media.saveAdminDraft',
  creatorDraft: 'admin.resources.media.saveCreatorDraft',
  published: 'admin.resources.media.savePublished',
}

const mediaResource = (
  api: Api,
  t: TFunction
): ResourceType<Media, MediaInput, MediaFormValues> => ({
  pluralModelName: t('admin.resources.media.namePlural'),
  singularModelName: t('admin.resources.media.nameSingular'),
  title: ({ caption }) => caption ?? '',
  columns: [
    createdAtColumn(t),
    {
      headerName: t('admin.resources.media.fields.position'),
      field: 'position',
      type: ['number'],
      // See the documentation for `comparator` in the `ag-grid`
      // documentation. This is customized to get "nulls last"
      // behavior, and also to make ascending/descending match what
      // happens in the shopper app (lowest numbers first).
      comparator: (
        _valueA: any,
        _valueB: any,
        nodeA: IRowNode<Media>,
        nodeB: IRowNode<Media>,
        isDescending: boolean
      ) => {
        const positionA = nodeA.data?.position
        const positionB = nodeB.data?.position

        if (positionA == null && positionB == null) {
          return 0
        } else if (positionA == null) {
          // Sort null either before or after depending on
          // `isDescending`, so that null positions always appear last
          // when the table is sorted by position.
          return isDescending ? -1 : 1
        } else if (positionB == null) {
          return isDescending ? 1 : -1
        } else if (positionA > positionB) {
          return 1
        } else if (positionA < positionB) {
          return -1
        } else {
          return 0
        }
      },
    },
    {
      headerName: t('admin.resources.media.fields.creator'),
      field: 'user.username',
    },
    {
      headerName: t('admin.resources.media.fields.caption'),
      field: 'caption',
    },
    {
      headerName: t('admin.resources.media.fields.score'),
      field: 'score',
      type: ['number'],
      valueFormatter: ({ data }) => formatScore(data?.score),
    },
    {
      headerName: t('admin.resources.media.fields.isFeatured'),
      field: 'user.isFeatured',
    },
    {
      headerName: t('admin.resources.media.fields.views'),
      field: 'views',
      type: ['number'],
    },
    {
      headerName: t('admin.resources.media.fields.saves'),
      field: 'saves',
      type: ['number'],
    },
    idColumn(t),
  ],
  api: api.media,
  IndexExtraButtons: MediaExtraButtons,
  Form: MediaForm,
  saveButtonTitle: ({ state }) => t(saveButtonTranslationStrings[state] as any),
  emptyInput: (query) => ({
    caption: query,
    state: 'published',
  }),
  inputFromExisting: ({ position, productIds, tagIds, ...model }) => ({
    ...model,

    position: position?.toString(),

    productId1: productIds[0],
    productId2: productIds[1],
    productId3: productIds[2],
    productId4: productIds[3],
    productId5: productIds[4],
    productId6: productIds[5],

    tagId1: tagIds[0],
    tagId2: tagIds[1],
    tagId3: tagIds[2],
  }),
  formValidator: {
    userId: presentNumberValidator(
      t('admin.resources.media.fields.creator'),
      t
    ),
    content: presentValidator(t('admin.resources.media.fields.content'), t),
  },
  formValuesToModelInput: ({
    userId: creatorId,
    state,
    position,
    caption,
    content,
    socialMediaPostId,
    productId1,
    productId2,
    productId3,
    productId4,
    productId5,
    productId6,
    tagId1,
    tagId2,
    tagId3,
  }) => ({
    userId: creatorId!,
    state,
    position: position ? parseInt(position) : null,
    caption: caption!,
    content: content!,
    socialMediaPostId: socialMediaPostId ?? null,
    productIds: uniq(
      compact([
        productId1,
        productId2,
        productId3,
        productId4,
        productId5,
        productId6,
      ])
    ),
    tagIds: uniq(compact([tagId1, tagId2, tagId3])),
  }),
  availableToCreators: true,
})

const priceStringFromCents = (cents: number | null) =>
  cents != null ? (cents / 100).toString() : undefined

const formatScore = (value: number | undefined | null) =>
  value ? value.toFixed(4).toString() : ''

const productsResource = (
  api: Api,
  config: Config,
  t: TFunction
): ResourceType<
  ProductWithImageAndRelatedProductIds,
  ProductInput,
  ProductFormValues
> => ({
  pluralModelName: t('admin.resources.products.namePlural'),
  singularModelName: t('admin.resources.products.nameSingular'),
  title: ({ brandName, name }) =>
    t('admin.resources.products.displayName', { brandName, name }),
  columns: [
    {
      headerName: t('admin.resources.products.fields.brand'),
      field: 'brandName',
    },
    {
      headerName: t('admin.resources.products.fields.name'),
      field: 'name',
    },
    {
      headerName: t('admin.resources.products.fields.convertedUrl'),
      valueGetter: ({ data }) => data && new URL(data.url).hostname,
    },
    {
      headerName: t('admin.resources.products.fields.views'),
      field: 'views',
      type: ['number'],
    },
    {
      headerName: t('admin.resources.products.fields.outbounds'),
      field: 'outgoingClicks',
      type: ['number'],
    },
    {
      headerName: t('admin.resources.products.fields.saves'),
      field: 'saves',
      type: ['number'],
    },
    {
      headerName: t('admin.resources.products.fields.curated'),
      valueGetter: ({ data }) => !!data && data.relatedProductIds.length > 0,
      width: 100,
    },
    {
      headerName: t('admin.resources.products.fields.resolvedUrlDomain'),
      valueGetter: ({ data }) =>
        data &&
        new URL(data.resolvedUrl ?? data.originalUrl ?? data.url).hostname,
    },
    createdAtColumn(t),
    idColumn(t),
  ],
  api: api.products,
  IndexExtraButtons: ProductsExtraButtons,
  IndexExtraHeader: ProductsExtraHeader,
  Form: ProductForm,
  emptyInput: (query) => ({
    name: query,
  }),
  inputFromExisting: ({ price, relatedProductIds, ...model }) => ({
    ...model,
    price: priceStringFromCents(price),
    relatedProductId1: relatedProductIds[0],
    relatedProductId2: relatedProductIds[1],
    relatedProductId3: relatedProductIds[2],
  }),
  formValidator: productValidator(config, t, 'admin'),
  formValuesToModelInput: ({
    productUrlId,
    brandId,
    category,
    name,
    url,
    price,
    imageId,
    relatedProductId1,
    relatedProductId2,
    relatedProductId3,
  }) => ({
    productUrlId: productUrlId ?? null,
    brandId: brandId!,
    category: category || null,
    name: name!,
    url: url!,
    price: priceStringToCents(price),
    imageId: imageId!,
    relatedProductIds: uniq(
      compact([relatedProductId1, relatedProductId2, relatedProductId3])
    ),
  }),
  editableByCreators: true,
})

const creatorLaunchImagesResource = (
  api: Api,
  t: TFunction
): ResourceType<
  CreatorLaunchImage,
  CreatorLaunchImageInput,
  Partial<CreatorLaunchImageInput>
> => ({
  pluralModelName: t('admin.resources.creatorLaunchImages.namePlural'),
  singularModelName: t('admin.resources.creatorLaunchImages.nameSingular'),
  title: () => t('admin.resources.creatorLaunchImages.displayName'),
  columns: [idColumn(t), createdAtColumn(t)],
  api: api.creatorLaunchImages,
  Form: CreatorLaunchImageForm,
  emptyInput: () => ({}),
  inputFromExisting: (x) => x,
  formValidator: {
    imageId: presentNumberValidator(
      t('admin.resources.creatorLaunchImages.fields.imageId'),
      t
    ),
  },
  formValuesToModelInput: ({ imageId }) => ({
    imageId: imageId!,
  }),
})

const tagsResource = (
  api: Api,
  t: TFunction
): ResourceType<Tag, TagInput, TagFormValues> => ({
  pluralModelName: t('admin.resources.tags.namePlural'),
  singularModelName: t('admin.resources.tags.nameSingular'),
  title: ({ name }) => name,
  columns: [
    {
      headerName: t('admin.resources.tags.fields.name'),
      field: 'name',
    },
    idColumn(t),
    createdAtColumn(t),
  ],
  api: api.tags,
  Form: TagForm,
  emptyInput: (query) => ({
    name: query,
    includeFeaturesInFeed: true,
    usedForCreatorFund: false,
  }),
  inputFromExisting: (model) => ({
    ...model,
    position: model.position?.toString(),
  }),
  formValidator: {
    name: presentStringValidator(t('admin.resources.tags.fields.name'), t),
  },
  formValuesToModelInput: ({
    name,
    position,
    notificationMessage,
    includeFeaturesInFeed,
    usedForCreatorFund,
  }) => ({
    name: name!,
    position: position ? parseInt(position) : null,
    notificationMessage,
    includeFeaturesInFeed: includeFeaturesInFeed!,
    usedForCreatorFund: usedForCreatorFund!,
  }),
})

const brandFeaturesResource = (
  api: Api,
  t: TFunction
): ResourceType<
  BrandFeature,
  BrandFeatureInput,
  Partial<BrandFeatureInput>
> => ({
  pluralModelName: t('admin.resources.brandFeatures.namePlural'),
  singularModelName: t('admin.resources.brandFeatures.nameSingular'),
  title: ({ brandId }) => brandId.toString(),
  columns: [
    {
      headerName: t('admin.resources.brandFeatures.fields.brandId'),
      field: 'brandId',
    },
    idColumn(t),
    createdAtColumn(t),
  ],
  api: api.brandFeatures,
  Form: BrandFeatureForm,
  inputFromExisting: (model) => model,
  formValuesToModelInput: ({ brandId, imageId }) => ({
    brandId: brandId!,
    imageId: imageId!,
  }),
  emptyInput: () => ({}),
  formValidator: {
    brandId: presentNumberValidator(
      t('admin.resources.brandFeatures.fields.brandId'),
      t
    ),
    imageId: presentNumberValidator(
      t('admin.resources.creatorFeatures.fields.imageId'),
      t
    ),
  },
})

const creatorFeaturesResource = (
  api: Api,
  t: TFunction
): ResourceType<
  CreatorFeature,
  CreatorFeatureInput,
  Partial<CreatorFeatureInput>
> => ({
  pluralModelName: t('admin.resources.creatorFeatures.namePlural'),
  singularModelName: t('admin.resources.creatorFeatures.nameSingular'),
  title: ({ userId }) => userId.toString(),
  columns: [
    {
      headerName: t('admin.resources.creatorFeatures.fields.userId'),
      field: 'userId',
    },
    idColumn(t),
    createdAtColumn(t),
  ],
  api: api.creatorFeatures,
  Form: CreatorFeatureForm,
  inputFromExisting: (model) => model,
  formValuesToModelInput: ({ userId, imageId }) => ({
    userId: userId!,
    imageId: imageId!,
  }),
  emptyInput: () => ({}),
  formValidator: {
    userId: presentNumberValidator(
      t('admin.resources.creatorFeatures.fields.userId'),
      t
    ),
    imageId: presentNumberValidator(
      t('admin.resources.creatorFeatures.fields.imageId'),
      t
    ),
  },
})

const productFeaturesResource = (
  api: Api,
  t: TFunction
): ResourceType<
  ProductFeature,
  ProductFeatureInput,
  Partial<ProductFeatureInput>
> => ({
  pluralModelName: t('admin.resources.productFeatures.namePlural'),
  singularModelName: t('admin.resources.productFeatures.nameSingular'),
  title: ({ productId }) => productId.toString(),
  columns: [
    {
      headerName: t('admin.resources.productFeatures.fields.product'),
      field: 'productId',
    },
    idColumn(t),
    createdAtColumn(t),
  ],
  api: api.productFeatures,
  Form: ProductFeatureForm,
  inputFromExisting: (model) => model,
  formValuesToModelInput: ({ productId }) => ({
    productId: productId!,
  }),
  emptyInput: () => ({}),
  formValidator: {
    productId: presentNumberValidator(
      t('admin.resources.productFeatures.fields.product'),
      t
    ),
  },
})

const labelsResource = (
  api: Api,
  t: TFunction
): ResourceType<Label, LabelInput, LabelFormValues> => ({
  pluralModelName: t('admin.resources.labels.namePlural'),
  singularModelName: t('admin.resources.labels.nameSingular'),
  title: ({ name }) => name,
  columns: [
    {
      headerName: t('admin.resources.labels.fields.name'),
      field: 'name',
    },
    {
      headerName: t(
        'admin.resources.labels.fields.useForProductRecommendations'
      ),
      field: 'useForProductRecommendations',
    },
    idColumn(t),
  ],
  api: api.labels,
  Form: LabelForm,
  inputFromExisting: (model) => model,
  formValuesToModelInput: ({ name, useForProductRecommendations }) => ({
    name: name!,
    useForProductRecommendations,
  }),
  emptyInput: () => ({
    useForProductRecommendations: true,
  }),
  formValidator: {
    name: presentStringValidator('admin.resources.labels.fields.name', t),
  },
})

const redemptionRequestResource = (
  api: Api,
  t: TFunction
): ResourceType<
  RedemptionRequest,
  RedemptionRequestInput,
  RedemptionRequestFormValues
> => ({
  pluralModelName: t('admin.resources.redemptionRequests.namePlural'),
  singularModelName: t('admin.resources.redemptionRequests.nameSingular'),
  title: ({ id }) => id.toString(),
  columns: [
    {
      headerName: t('admin.resources.redemptionRequests.fields.username'),
      field: 'username',
    },
    {
      headerName: t('admin.resources.redemptionRequests.fields.inputPrice'),
      field: 'inputPrice',
      type: ['number'],
    },
    {
      headerName: t('admin.resources.redemptionRequests.fields.actualPrice'),
      field: 'actualPrice',
      type: ['number'],
    },
    {
      headerName: t('admin.resources.redemptionRequests.fields.status'),
      field: 'status',
    },
    createdAtColumn(t),
  ],
  api: api.redemptionRequest,
  Form: RedemptionRequestForm,
  inputFromExisting: ({ inputPrice, actualPrice, ...model }) => ({
    ...model,
    inputPrice: inputPrice.toString(),
    actualPrice: actualPrice.toString(),
  }),
  formValuesToModelInput: ({
    userId,
    url,
    inputPrice,
    actualPrice,
    size,
    variant,
    email,
    shippingAddress,
    notes,
    status,
  }) => ({
    userId: userId!,
    url: url!,
    inputPrice: parseFloat(inputPrice ?? '0'),
    actualPrice: parseFloat(actualPrice ?? '0'),
    size,
    variant,
    email: email!,
    shippingAddress: shippingAddress!,
    notes,
    status,
  }),
  emptyInput: () => ({ status: 'unprocessed' }),
  formValidator: {
    url: presentUrlValidator(
      t('admin.resources.redemptionRequests.fields.url'),
      t
    ),
    inputPrice: presentStringValidator(
      t('admin.resources.redemptionRequests.fields.inputPrice'),
      t
    ),
    email: presentStringValidator(
      t('admin.resources.redemptionRequests.fields.email'),
      t
    ),
    shippingAddress: presentStringValidator(
      t('admin.resources.redemptionRequests.fields.shippingAddress'),
      t
    ),
    status: presentStringValidator(
      t('admin.resources.redemptionRequests.fields.status'),
      t
    ),
  },
})

export const makeResources = (
  api: Api,
  config: Config,
  t: TFunction
): Record<ResourceName, ResourceType<any, any, any>> => ({
  affiliateDomains: affiliateDomainsResource(api, t),
  brands: brandsResource(api, t),
  brandFeatures: brandFeaturesResource(api, t),
  categories: categoriesResource(api, t),
  edits: editsResource(api, t),
  users: usersResource(api, t),
  media: mediaResource(api, t),
  products: productsResource(api, config, t),
  creatorLaunchImages: creatorLaunchImagesResource(api, t),
  creatorFeatures: creatorFeaturesResource(api, t),
  productFeatures: productFeaturesResource(api, t),
  tags: tagsResource(api, t),
  labels: labelsResource(api, t),
  redemptionRequests: redemptionRequestResource(api, t),
})
